import { useOrganization } from '@clerk/clerk-react';
import { CircleCheck, Loader2, LockKeyhole } from 'lucide-react';
import { useEffect, useState } from 'react';

import { HOME_TOUR_CLASSES } from '@/constants/tour/classNames';
import { useCreateMyDiscovery } from '@/hooks/my-discovery/useCreateMyDiscovery';
import { useUpdateMyDiscovery } from '@/hooks/my-discovery/useUpdateMyDiscovery';
import { toast } from '@/hooks/ui/use-toast';
import { cn } from '@/lib/ui/utils';
import { useLinkClickStore } from '@/stores/stages/useLinkClickStore';
import { useStageItemStore } from '@/stores/stages/useStageItemStore';
import { useStageStore } from '@/stores/stages/useStageStore';
import { useStagePromptsStore } from '@/stores/useStagePromptsStore';
import { DiscoveryStatus } from '@/types/discovery.status.type';
import { StageItem } from '@/types/stage.item.type';

import { CongratulationsDialog } from '../dialogs/CongratulationsDialog';
import { StageCompletionDialog } from '../dialogs/StageCompletionDialog';
import { UploadZeeDocumentDialog } from '../dialogs/UploadZeeDocumentDialog';
import { Checkbox } from '../ui/checkbox';
import { ReferButton } from './ReferButton';

interface StageItemCardProps {
  index: number;
  status: 'completed' | 'active' | 'locked';
  stageItem: StageItem;
  className?: string;
}

const EXTERNAL_LINK_TYPES = [
  'document-sign',
  'video-link',
  'meeting-link',
  'view-document',
  'external-web-link',
] as const;

const StageItemCard = ({
  index,
  status,
  stageItem,
  className = '',
}: StageItemCardProps) => {
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [showStageCompletion, setShowStageCompletion] = useState(false);

  const { organization } = useOrganization();
  const { stageItems } = useStageItemStore();
  const { stageList, discoveryStatus, setDiscoveryStatus } = useStageStore();
  const { hasLinkBeenClicked } = useLinkClickStore();
  const { stagePrompts } = useStagePromptsStore();

  const {
    mutate: completeStageItem,
    result: completeStageItemResult,
    isError: completeStageItemIsError,
    isLoading: completeStageItemIsLoading,
    isSuccess: completeStageItemIsSuccess,
  } = useCreateMyDiscovery();

  const {
    mutate: updateStageItem,
    result: updateStageItemResult,
    isError: updateStageItemIsError,
    isLoading: updateStageItemIsLoading,
    isSuccess: updateStageItemIsSuccess,
  } = useUpdateMyDiscovery();

  const isLastStageItem = (stageItemId: string) =>
    stageItems.findIndex((item) => item.id === stageItemId) ===
    stageItems.length - 1;

  const getCompletedStageIds = (stageItemId: string) =>
    isLastStageItem(stageItemId) ? [stageItem.stage_id ?? ''] : [];

  const checkIfAllStagesCompleted = (completedStageIds: string[]) => {
    const totalStages = stageList.length;
    const completedStagesCount = completedStageIds.length;
    return totalStages > 0 && completedStagesCount === totalStages;
  };

  const checkIfAllItemsInStageCompleted = (
    stageId: string,
    completedItemIds: string[]
  ) => {
    const stageItemsForCurrentStage = stageItems.filter(
      (item) => item.stage_id === stageId
    );
    const completedItemsInStage = stageItemsForCurrentStage.filter((item) =>
      completedItemIds.includes(item.id ?? '')
    );
    return (
      stageItemsForCurrentStage.length > 0 &&
      completedItemsInStage.length === stageItemsForCurrentStage.length
    );
  };

  const checkIfLinkRequired = (stageItem: StageItem) => {
    // Check if this stage item has a link that needs to be clicked
    const hasExternalLink =
      EXTERNAL_LINK_TYPES.includes(
        stageItem.type as (typeof EXTERNAL_LINK_TYPES)[number]
      ) && stageItem.external_link;

    const hasInternalLink =
      stageItem.type === 'link-to-tab' && stageItem.internal_tab;

    return hasExternalLink || hasInternalLink;
  };

  const canCompleteStageItem = (stageItemId: string) => {
    // If the stage item requires a link click, check if it has been clicked
    if (checkIfLinkRequired(stageItem)) {
      return hasLinkBeenClicked(stageItemId);
    }
    // For items without links (like uploads), allow completion
    return true;
  };

  const handleCompleteStageItem = (stageItemId: string) => {
    const completedStageIds = getCompletedStageIds(stageItemId);

    if (discoveryStatus) {
      updateStageItem({
        completed_stage_ids: [
          ...discoveryStatus.completed_stage_ids,
          ...completedStageIds,
        ],
        completed_stage_item_ids: [
          ...discoveryStatus.completed_stage_item_ids,
          stageItemId,
        ],
      });
    } else {
      completeStageItem({
        completed_stage_ids: completedStageIds,
        completed_stage_item_ids: [stageItemId],
      });
    }
  };

  const canUncompleteStageItem = (stageItemId: string) => {
    if (!discoveryStatus) return false;

    // Must be currently completed
    const completedIds = discoveryStatus.completed_stage_item_ids;
    if (!completedIds.includes(stageItemId)) return false;

    // Only allow unchecking the most recently completed item globally
    const lastCompletedId = completedIds[completedIds.length - 1];
    return stageItemId === lastCompletedId;
  };

  const handleUncompleteStageItem = (stageItemId: string) => {
    if (!discoveryStatus) return;

    // Check if this item can be uncompleted (backward one step restriction)
    if (!canUncompleteStageItem(stageItemId)) {
      toast({
        title: 'Cannot uncheck this item',
        description:
          'You can only uncheck the item just before the current active item. If all items are completed, you can only uncheck the last item.',
        variant: 'destructive',
      });
      return;
    }

    updateStageItem({
      // Uncompleting any item makes its stage incomplete
      completed_stage_ids: discoveryStatus.completed_stage_ids.filter(
        (id) => id !== (stageItem.stage_id ?? '')
      ),
      completed_stage_item_ids: discoveryStatus.completed_stage_item_ids.filter(
        (id) => id !== stageItemId
      ),
    });
  };

  const handleToggleStageItem = (stageItemId: string) => {
    if (status === 'completed') {
      handleUncompleteStageItem(stageItemId);
    } else if (status === 'active') {
      // Check if link needs to be clicked first
      if (!canCompleteStageItem(stageItemId)) {
        toast({
          title: 'Link must be clicked first',
          description:
            'Please click the link button before marking this item as complete.',
          variant: 'destructive',
        });
        return;
      }
      handleCompleteStageItem(stageItemId);
    }
  };

  useEffect(() => {
    if (completeStageItemIsError || updateStageItemIsError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later',
        variant: 'destructive',
      });
    }
  }, [completeStageItemIsError, updateStageItemIsError]);

  useEffect(() => {
    if (!completeStageItemIsSuccess && !updateStageItemIsSuccess) return;

    const newStatus: DiscoveryStatus | undefined =
      completeStageItemResult?.data || updateStageItemResult?.data;

    if (newStatus) {
      // Check if this action caused all stages to be completed
      const wasAllStagesCompleted = checkIfAllStagesCompleted(
        discoveryStatus?.completed_stage_ids || []
      );
      const isAllStagesCompletedNow = checkIfAllStagesCompleted(
        newStatus.completed_stage_ids
      );

      // Check if this action caused all items in the current stage to be completed
      const currentStageId = stageItem.stage_id ?? '';
      const wasAllItemsInStageCompleted = checkIfAllItemsInStageCompleted(
        currentStageId,
        discoveryStatus?.completed_stage_item_ids || []
      );
      const isAllItemsInStageCompletedNow = checkIfAllItemsInStageCompleted(
        currentStageId,
        newStatus.completed_stage_item_ids
      );

      setDiscoveryStatus(newStatus);

      // Show stage completion celebration if all items in stage are completed
      if (!wasAllItemsInStageCompleted && isAllItemsInStageCompletedNow) {
        // Completion tracking is handled by a DB trigger on discovery status updates.

        // Small delay to ensure UI updates are complete
        setTimeout(() => {
          setShowStageCompletion(true);
        }, 500);
      }
      // Show all stages congratulations if all stages are completed
      else if (!wasAllStagesCompleted && isAllStagesCompletedNow) {
        // Small delay to ensure UI updates are complete
        setTimeout(() => {
          setShowCongratulations(true);
        }, 500);
      }
    }
  }, [
    stageList,
    stageItem.stage_id,
    updateStageItemResult,
    completeStageItemResult,
    updateStageItemIsSuccess,
    completeStageItemIsSuccess,
    setDiscoveryStatus,
  ]);

  const getButtonLink = () => {
    const { type, external_link, internal_tab } = stageItem;
    if (!type) return undefined;

    if (
      EXTERNAL_LINK_TYPES.includes(type as (typeof EXTERNAL_LINK_TYPES)[number])
    ) {
      return external_link ?? '';
    }

    if (type === 'link-to-tab') {
      return internal_tab ?? '';
    }

    return undefined;
  };

  const isLoading = completeStageItemIsLoading || updateStageItemIsLoading;

  return (
    <>
      <div
        className={cn(
          'flex justify-between gap-4 rounded-xl border-2 px-6 py-5 max-md:p-3',
          status === 'completed' && 'items-center border-zinc-300',
          status === 'active' && 'border-primary max-md:flex-col',
          status === 'locked' && 'items-center border-zinc-200',
          className
        )}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            {status === 'completed' &&
              (isLoading ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <Checkbox
                  checked
                  className={cn(
                    'size-5',
                    !canUncompleteStageItem(stageItem.id ?? '') &&
                      'cursor-not-allowed opacity-50'
                  )}
                  onCheckedChange={() =>
                    handleToggleStageItem(stageItem.id ?? '')
                  }
                  disabled={!canUncompleteStageItem(stageItem.id ?? '')}
                />
              ))}
            {status === 'locked' && (
              <LockKeyhole size={20} className="text-typo-gray-tertiary" />
            )}
            {status === 'active' &&
              (isLoading ? (
                <Loader2 className="size-5 flex-shrink-0 animate-spin" />
              ) : (
                <Checkbox
                  checked={false}
                  className={`${HOME_TOUR_CLASSES.STAGE_ITEM_CHECKBOX} size-5 ${
                    stageItem.type === 'brand-review'
                      ? 'cursor-not-allowed opacity-50'
                      : ''
                  }`}
                  onCheckedChange={() =>
                    handleToggleStageItem(stageItem.id ?? '')
                  }
                  disabled={stageItem.type === 'brand-review'}
                />
              ))}
            <p
              className={cn(
                'as-title-01 as-strong',
                (status === 'completed' || status === 'locked') &&
                  'text-typo-gray-tertiary'
              )}
            >
              {`${index + 1}. ${stageItem.title}`}
            </p>
          </div>

          {status === 'active' && (
            <div className="space-y-3">
              <p className="as-body-03 text-zinc-800">
                {stageItem.description}
              </p>
              {checkIfLinkRequired(stageItem) && (
                <div className="flex items-center gap-2">
                  {hasLinkBeenClicked(stageItem.id ?? '') ? (
                    <div className="flex items-center gap-1 text-green-600">
                      <CircleCheck size={14} />
                      <span className="text-xs">Link visited</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-amber-600">
                      <LockKeyhole size={14} />
                      <span className="text-xs">
                        Click link first to enable checkbox
                      </span>
                    </div>
                  )}
                </div>
              )}
              {stageItem.type === 'brand-review' && (
                <div className="flex items-center gap-1 text-amber-600">
                  <LockKeyhole size={14} />
                  <span className="text-xs">
                    Brand review required. The brand team must approve this
                    item.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        {status === 'completed' && (
          <div className="flex items-center gap-2 max-sm:hidden">
            <CircleCheck size={24} className="text-green-600" />
            <p className="as-title-03 text-green-600">Completed!</p>
          </div>
        )}
        {status === 'active' && (
          <ReferButton
            link={getButtonLink()}
            type={stageItem.type ?? ''}
            isUpload={stageItem.type === 'upload'}
            className={`${HOME_TOUR_CLASSES.REFER_BUTTON} max-md:w-full`}
            openUploadDialog={() => setOpenUploadDialog(true)}
            stageItemId={stageItem.id}
            stagePrompt={stagePrompts?.find(
              (prompt) => prompt.id === stageItem.aimei_prompt_id
            )}
          />
        )}
      </div>

      <UploadZeeDocumentDialog
        open={openUploadDialog}
        stage={stageList.find((stage) => stage.id === stageItem.stage_id) || {}}
        stageItem={stageItem}
        setOpen={setOpenUploadDialog}
      />

      <StageCompletionDialog
        open={showStageCompletion}
        onOpenChange={setShowStageCompletion}
        stage={stageList.find((stage) => stage.id === stageItem.stage_id) || {}}
        stageList={stageList}
        completedItemsCount={
          stageItems.filter(
            (item) =>
              item.stage_id === stageItem.stage_id &&
              discoveryStatus?.completed_stage_item_ids.includes(item.id ?? '')
          ).length
        }
        totalItemsCount={
          stageItems.filter((item) => item.stage_id === stageItem.stage_id)
            .length
        }
        brandName={organization?.name}
      />

      <CongratulationsDialog
        open={showCongratulations}
        onOpenChange={setShowCongratulations}
      />
    </>
  );
};

export { StageItemCard };
